import React, { createContext, useContext } from "react";

import { AuthEndpoints } from "@generated/auth";
import {
  UserProvider as LegacyUserProvider,
  UserContext as LegacyUserContext,
} from "src/domains/auth/contexts/UserContext";
import { FeatureFlagNames } from "src/platform/constants/FeatureFlags";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { StorageKeys } from "src/platform/constants/Storage";
import { featureFlags } from "src/platform/feature-flags/FeatureFlags";
import API from "src/platform/http/shared-http";
import debugLogger from "src/platform/utils/debugLogger";

import { useAuth } from "../hooks/useAuth";
import { AuthState, LoginCredentials, User } from "../types/auth.types";

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => void;
  logout: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  loginWithOkta?: (authState: any) => void;
  processOAuthToken?: (token: string) => void;
  setAuthInProgress?: (status: boolean) => void;
  authInProgress?: boolean;
  isOktaAuthenticated?: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const isNewAuthEnabled = featureFlags.isEnabled(
    FeatureFlagNames.NEW_AUTH_MODULE,
  );

  if (!isNewAuthEnabled) {
    return <LegacyUserProvider>{children}</LegacyUserProvider>;
  }

  return <EventDrivenAuthProvider>{children}</EventDrivenAuthProvider>;
};

const EventDrivenAuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const authState = useAuth();

  const contextValue: AuthContextType = {
    ...authState,
    login: authState.login,
    logout: authState.logout,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    loginWithOkta: (_authState: any) => {
      // Okta token is handled externally; persist the token it provides
      debugLogger.info(
        "auth-provider",
        "loginWithOkta called — delegating to legacy flow",
      );
    },
    processOAuthToken: (_token: string) => {
      // The session JWT lives in an httpOnly cookie set by the API; we do not
      // store the token in JS-accessible storage. Auth state resolves via
      // fetchCurrentUser (/auth/me).
      debugLogger.info("auth-provider", "processOAuthToken — cookie-based");
    },
    setAuthInProgress: (_status: boolean) => {
      // loading state is managed internally by useAuth
    },
    authInProgress: authState.loading,
    isOktaAuthenticated: !!localStorage.getItem(StorageKeys.OKTA_AUTH),
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

export const useLegacyAuth = () => {
  const isNewAuthEnabled = featureFlags.isEnabled(
    FeatureFlagNames.NEW_AUTH_MODULE,
  );

  const newAuthContext = useContext(AuthContext);
  const legacyContext = useContext(LegacyUserContext);

  if (isNewAuthEnabled && newAuthContext) {
    const newAuth = newAuthContext;

    return {
      user: newAuth.user,
      setUser: (user: User) => {
        localStorage.setItem(StorageKeys.USER, JSON.stringify(user));
        debugLogger.info("use-legacy-auth", "setUser called", user.email);
      },
      login: async (credentials: LoginCredentials) => {
        newAuth.login(credentials);
      },
      signUp: async (userData: {
        firstName: string;
        lastName: string;
        email: string;
        password: string;
      }) => {
        const api = new API(apiBaseUrls.BACKEND, AuthEndpoints.SIGNUP);
        await api.post(userData);
        debugLogger.info(
          "use-legacy-auth",
          "signUp successful",
          userData.email,
        );
      },
      loginWithOkta: newAuth.loginWithOkta || (() => {}),
      processOAuthToken: newAuth.processOAuthToken || (() => {}),
      logout: () => newAuth.logout(),
      loading: newAuth.loading,
      error: newAuth.error,
      isOktaAuthenticated: newAuth.isOktaAuthenticated ?? false,
      authInProgress: newAuth.authInProgress ?? false,
      setAuthInProgress: newAuth.setAuthInProgress || (() => {}),
    };
  } else {
    return legacyContext;
  }
};

export default AuthProvider;
